
import { ReactNode, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

interface TokenRouteProps {
  children: ReactNode;
}

/**
 * TokenRoute allows access to token-based routes like the Welcome page
 * regardless of authentication status, but ensures a token parameter is present
 * OR the user has appropriate role claims (including guest access via custom claims)
 */
const TokenRoute = ({ children }: TokenRouteProps) => {
  const { loading, userProfile, currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if we have a token in the URL or appropriate access
  useEffect(() => {
    if (loading) return;
    
    let cancelled = false;
    
    const checkAccess = async () => {
      const params = new URLSearchParams(location.search);
      const token = params.get('token');
      
      console.log("TokenRoute: Checking for token. Path:", location.pathname, "Token:", token ? "present" : "missing");

      const isWelcome = location.pathname === '/welcome';
      const isRoot = location.pathname === '/';
      
      // Check for privileged roles from user profile (permanent users)
      const role = (userProfile?.role as string | undefined) || undefined;
      const isPrivileged = role === 'admin_user' || role === 'camp_manager'  || role === 'camp_contributor';
      
      // Check for guest access via custom claims (anonymous users with invitation claims)
      let hasAnonGuestAccess = false;
      if (currentUser?.isAnonymous) {
        try {
          // Force refresh to get latest claims
          const tokenResult = await currentUser.getIdTokenResult(true);
          const claimsRole = tokenResult.claims.role;
          const claimsOrgId = tokenResult.claims.organizationId;
          hasAnonGuestAccess = Boolean(claimsRole && claimsOrgId);
          
          if (hasAnonGuestAccess) {
            console.log("Anonymous user has guest access via custom claims, allowing access to", location.pathname);
          }
        } catch (error) {
          console.error("Error checking custom claims:", error);
          hasAnonGuestAccess = false;
        }
      }
      
      // Prevent state updates if component was unmounted
      if (cancelled) return;

      // Handle anonymous users on welcome/root paths
      if (currentUser?.isAnonymous && (isWelcome || isRoot)) {
        if (hasAnonGuestAccess) {
          return; // Allow access
        } else if (!token) {
          console.log("Anonymous user without token or claims, redirecting to login");
          toast.error("No invitation token provided");
          navigate('/login');
          return;
        }
      } else if ((isWelcome || isRoot) && !token && !isPrivileged) {
        console.log("No token provided for welcome/root route and user not privileged, redirecting to login");
        toast.error("No invitation token provided");
        navigate('/login');
        return;
      } else if (token && isRoot) {
        // If we're at root path with a token, redirect to /welcome for consistency
        console.log("Found token on root path, redirecting to /welcome");
        navigate(`/welcome?token=${token}`, { replace: true });
        return;
      } else if (token || isPrivileged) {
        console.log("Token or privileged role present, allowing access to", location.pathname);
      }
    };
    
    checkAccess();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      cancelled = true;
    };
  }, [loading, location.pathname, location.search, navigate, currentUser, userProfile]);
  
  if (loading) {
    // Show nothing while auth state is being determined
    console.log("TokenRoute: Auth is still loading");
    return null;
  }
  
  // Allow access to the page regardless of authentication status
  console.log("TokenRoute: Rendering children");
  return <>{children}</>;
};

export default TokenRoute;
